import { useEffect, useState } from 'react';
import { getPublicCohort } from '../../services/circles.service';
import type { PendingCircleJoin } from './pendingCircleJoin';
import { deviceClock } from '../../utils/booking';

/** The note's words (no i18n layer; the NOTICE_COPY pattern). No dashes. */
export const PENDING_JOIN_COPY = {
  register: (target: string) =>
    `Create your account to join ${target}. We'll take you there after you verify your email.`,
  login: (target: string) => `Log in to join ${target}. We'll take you there next.`,
  hourly: (time: string) => `the ${time} empathy circle`,
  unnamedGroup: 'the group',
} as const;

/** Still worth promising: no window known (a bare link), or the window not yet closed. */
function stillOpen(join: PendingCircleJoin, nowMs: number): boolean {
  return join.until === null || Date.parse(join.until) > nowMs;
}

/**
 * The line on /register and /login that says why the visitor is there when
 * they carry a pending circle join (docs/plans/CIRCLE-STARTING-NOTICE.md §5,
 * N12): the circle-starting notice's Join, or a /register?circle= link.
 * PendingCircleJoin takes them there once they are in and verified.
 *
 * The group's name comes from the public group endpoint (the stored join
 * holds numbers only, S4); it is rendered as text. Nothing shows while it
 * loads, a failed load still says "the group", and a 404 (no such public
 * group) shows nothing.
 */
export default function PendingJoinNote({
  join,
  mode,
}: {
  join: PendingCircleJoin | null;
  mode: 'register' | 'login';
}) {
  const open = join !== null && stillOpen(join, Date.now());
  const cohortId = open && join.kind === 'cohort' ? join.cohortId : null;
  // name null: the group answered 404 (missing or private), so nothing is promised (b-009).
  const [group, setGroup] = useState<{ id: number; name: string | null } | null>(null);

  useEffect(() => {
    if (cohortId === null) return;
    let cancelled = false;
    getPublicCohort(cohortId)
      .then((c) => {
        if (!cancelled) setGroup({ id: cohortId, name: c.name || PENDING_JOIN_COPY.unnamedGroup });
      })
      .catch((err: { response?: { status?: number } }) => {
        if (cancelled) return;
        // A 404 is the server saying there is no public group here; only a
        // failure to ask (network, 5xx) keeps the unnamed promise.
        const missing = err?.response?.status === 404;
        setGroup({ id: cohortId, name: missing ? null : PENDING_JOIN_COPY.unnamedGroup });
      });
    return () => {
      cancelled = true;
    };
  }, [cohortId]);

  if (!open || !join) return null;

  let target: string;
  if (join.kind === 'hourly') {
    const time = deviceClock(join.hourlyAt * 1000);
    target = PENDING_JOIN_COPY.hourly(time);
  } else {
    if (group === null || group.id !== join.cohortId || group.name === null) return null;
    target = group.name;
  }

  return (
    <p data-testid="pending-circle-join-note" className="ee-auth-invite" role="status">
      {mode === 'register' ? PENDING_JOIN_COPY.register(target) : PENDING_JOIN_COPY.login(target)}
    </p>
  );
}
